// Modal that shows the exported live event JSON so it can be copied/downloaded
// and imported into another environment (TEST/BETA/PROD).

import React from 'react';
import { Alert, Button, Modal, ModalProps, Spinner } from 'react-bootstrap';

type Props = {
  loading: boolean;
  json: string | null;
} & ModalProps;

const ExportLiveEventModal = ({ show, onHide, loading, json }: Props) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    // Reset the "Copied" hint whenever the modal is re-opened with new content.
    setCopied(false);
  }, [json, show]);

  const handleCopy = () => {
    if (!json) {
      return;
    }
    navigator.clipboard.writeText(json).then(
      () => setCopied(true),
      () => setCopied(false)
    );
  };

  const handleDownload = () => {
    if (!json) {
      return;
    }
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `live-event-export.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Export live event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          This is the event&apos;s content only (no dates). Copy or download it, then use{' '}
          <strong>Import live event</strong> in the target environment.
        </Alert>
        {loading || json === null ? (
          <Spinner animation="border" />
        ) : (
          <textarea
            className="form-control"
            readOnly
            rows={16}
            value={json}
            style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" disabled={loading || !json} onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy JSON'}
        </Button>
        <Button variant="success" disabled={loading || !json} onClick={handleDownload}>
          Download JSON
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportLiveEventModal;
